import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import { cn } from '../lib/utils';
import { avatarTint, iniciales } from '../lib/identity';
import { estadoDe } from '../lib/estados';
import { Clock, MoreVertical, User, AlertTriangle, Check, DoorOpen } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

/**
 * Tarjeta de paciente/cita.
 *
 * Cambios de diseño: el nombre ya NO se corta a media palabra (era el defecto
 * más visible de la lista), el estado es pastilla con punto de color, y las
 * acciones frecuentes (en sala / atendido) están a un clic sin abrir el menú.
 */
export const PatientCard = ({
  appointment,
  patient,
  onViewPatient,
  onUpdateStatus,
  compact = false
}) => {
  const estado = estadoDe(appointment?.estado);
  const alertas = patient?.alertas_medicas || [];
  const nombre = appointment?.paciente_nombre || (patient ? `${patient.nombre} ${patient.apellido}` : 'Paciente');

  if (compact) {
    return (
      <div
        className={cn(
          'group relative flex items-center gap-3 rounded-lg border border-border/60 bg-card px-3 py-2.5 transition-colors hover:border-primary/30 hover:bg-muted/40',
        )}
        data-testid={`patient-card-${appointment?.id || patient?.id}`}
      >
        <span
          className="absolute left-0 top-2 bottom-2 w-[3px] rounded-r-full"
          style={{ backgroundColor: appointment?.doctor_color || 'hsl(var(--primary))' }}
          aria-hidden="true"
        />

        <Avatar className="h-9 w-9 shrink-0">
          <AvatarImage src={patient?.avatar_url} />
          <AvatarFallback className="text-[11px] font-semibold text-white" style={avatarTint(nombre)}>
            {iniciales(nombre, 'P')}
          </AvatarFallback>
        </Avatar>

        <button
          type="button"
          className="min-w-0 flex-1 text-left"
          onClick={() => onViewPatient?.(patient || appointment)}
        >
          <p className="truncate text-[13px] font-semibold leading-tight">{nombre}</p>
          {appointment && (
            <p className="mt-0.5 flex items-center gap-1.5 text-[11.5px] text-muted-foreground">
              <Clock className="h-3 w-3 shrink-0" />
              <span className="tnum">{appointment.hora_inicio}</span>
              <span className="truncate">{appointment.motivo}</span>
            </p>
          )}
        </button>

        {alertas.length > 0 && (
          <AlertTriangle className="h-4 w-4 shrink-0 text-rose-500" aria-label="Tiene alertas médicas" />
        )}

        {appointment && (
          <span className={cn('inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-semibold ring-1 ring-inset', estado.pill)}>
            <span className={cn('h-1.5 w-1.5 rounded-full', estado.punto)} />
            {estado.label}
          </span>
        )}

        {appointment && onUpdateStatus && !['atendido', 'cancelada', 'no_asistio'].includes(appointment.estado) && (
          <div className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
            <Button
              variant="ghost" size="icon"
              className="h-7 w-7 text-warning hover:bg-warning/10 hover:text-warning"
              onClick={() => onUpdateStatus(appointment.id, 'en_sala')}
              aria-label="Pasar a sala"
            >
              <DoorOpen className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost" size="icon"
              className="h-7 w-7 text-success hover:bg-success/10 hover:text-success"
              onClick={() => onUpdateStatus(appointment.id, 'atendido')}
              aria-label="Marcar atendido"
            >
              <Check className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className="relative overflow-hidden rounded-xl border border-border/70 bg-card p-4 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md"
      data-testid={`patient-card-${appointment?.id || patient?.id}`}
    >
      {alertas.length > 0 && (
        <span className="absolute left-0 top-0 h-full w-[3px] bg-rose-500" aria-hidden="true" />
      )}

      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <Avatar className="h-11 w-11 shrink-0">
            <AvatarImage src={patient?.avatar_url} />
            <AvatarFallback className="text-[12px] font-semibold text-white" style={avatarTint(nombre)}>
              {iniciales(nombre, 'P')}
            </AvatarFallback>
          </Avatar>

          <div className="min-w-0">
            <p className="truncate text-[14px] font-semibold leading-tight">{nombre}</p>
            {appointment ? (
              <>
                <p className="mt-1 flex items-center gap-1.5 text-[12px] text-muted-foreground">
                  <Clock className="h-3.5 w-3.5 shrink-0" />
                  <span className="tnum">{appointment.hora_inicio} – {appointment.hora_fin}</span>
                </p>
                <p className="mt-0.5 truncate text-[12px] text-muted-foreground">{appointment.motivo}</p>
              </>
            ) : patient?.telefono ? (
              <p className="mt-1 truncate text-[12px] text-muted-foreground tnum">{patient.telefono}</p>
            ) : null}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-1.5">
          {appointment && (
            <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset', estado.pill)}>
              <span className={cn('h-1.5 w-1.5 rounded-full', estado.punto)} />
              {estado.label}
            </span>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground" data-testid="patient-menu-btn" aria-label="Acciones del paciente">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onViewPatient?.(patient || appointment)}>
                <User className="mr-2 h-4 w-4" /> Ver expediente
              </DropdownMenuItem>
              {appointment && onUpdateStatus && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => onUpdateStatus(appointment.id, 'en_sala')}>
                    <DoorOpen className="mr-2 h-4 w-4" /> En sala de espera
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onUpdateStatus(appointment.id, 'atendido')}>
                    <Check className="mr-2 h-4 w-4" /> Marcar atendido
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => onUpdateStatus(appointment.id, 'cancelada')}
                    className="text-destructive"
                  >
                    Cancelar cita
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {alertas.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5 border-t border-border/60 pt-3">
          {alertas.map((alerta, idx) => (
            <span
              key={idx}
              className="inline-flex items-center gap-1 rounded-full bg-rose-500/10 px-2 py-0.5 text-[11px] font-medium text-rose-700 ring-1 ring-inset ring-rose-500/25 dark:text-rose-300"
            >
              <AlertTriangle className="h-3 w-3" />
              {alerta.descripcion}
            </span>
          ))}
        </div>
      )}

      {appointment?.doctor_nombre && (
        <div className="mt-3 flex items-center gap-2 border-t border-border/60 pt-3">
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: appointment.doctor_color }} />
          <span className="text-[11.5px] text-muted-foreground">{appointment.doctor_nombre}</span>
        </div>
      )}
    </div>
  );
};
